/**
 * Cirro product hero: gallery thumbnails, bundle picker, and the buy button.
 *
 * Config comes from data attributes on the [data-cirro-product] root so the
 * same script serves the product page and the homepage copy of the section.
 */
(() => {
  const JSON_HEADERS = { 'Content-Type': 'application/json', Accept: 'application/json' };
  const formatMoney = (cents) => '$' + (cents / 100).toFixed(cents % 100 ? 2 : 0);

  /** Adds `n` business days to a date. */
  const addBusinessDays = (date, n) => {
    const d = new Date(date);
    let added = 0;
    while (added < n) {
      d.setDate(d.getDate() + 1);
      if (d.getDay() % 6) added++;
    }
    return d;
  };

  const init = (root) => {
    if (root.dataset.cirroReady) return;
    root.dataset.cirroReady = 'true';

    const form = root.querySelector('form.cirro-form');
    if (!form) return;

    const directCheckout = root.dataset.directCheckout === 'true';
    const idInput = form.querySelector('[name="id"]');
    const gift = root.querySelector('.cirro-gift[data-gift-id]');
    const message = root.querySelector('[data-cirro-msg]');
    const sticky = document.getElementById('CirroSticky');
    const mainButton = form.querySelector('.cirro-btn');
    const buttons = [mainButton, sticky?.querySelector('.cirro-btn')].filter(Boolean);
    const selectedBundle = () => root.querySelector('.cirro-bundle.is-selected');

    const setBusy = (busy) => buttons.forEach((b) => (b.disabled = busy));
    const showMessage = (text) => {
      if (!message) return;
      message.textContent = text || '';
      message.hidden = !text;
    };

    // Gallery
    const mainImage = root.querySelector('.cirro-gallery__main img');
    const thumbs = root.querySelectorAll('.cirro-gallery__thumbs button');
    thumbs.forEach((thumb) => {
      thumb.addEventListener('click', () => {
        if (mainImage) {
          mainImage.removeAttribute('srcset');
          mainImage.src = thumb.dataset.src;
        }
        thumbs.forEach((t) => t.classList.toggle('is-active', t === thumb));
      });
    });

    // Bundle picker
    const refresh = () => {
      const bundle = selectedBundle();
      if (!bundle) return;
      const qty = Number(bundle.dataset.qty);
      const price = formatMoney(Number(bundle.dataset.cents));
      [root, sticky].filter(Boolean).forEach((scope) => {
        scope.querySelectorAll('[data-cirro-price]').forEach((el) => (el.textContent = price));
      });
      root.querySelectorAll('[data-gift-qty]').forEach((el) => (el.textContent = qty));
      if (gift) {
        const total = formatMoney(Number(gift.dataset.giftValue) * qty);
        root.querySelectorAll('[data-gift-total]').forEach((el) => (el.textContent = total));
      }
    };
    const bundles = root.querySelectorAll('.cirro-bundle');
    bundles.forEach((card) => {
      card.addEventListener('click', () => {
        const radio = card.querySelector('input');
        radio.checked = true;
        idInput.value = radio.value;
        bundles.forEach((c) => c.classList.toggle('is-selected', c === card));
        showMessage('');
        refresh();
      });
    });

    // Buy button
    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      setBusy(true);
      showMessage('');

      const items = [{ id: Number(idInput.value), quantity: 1 }];
      // One free cover per cushion. The global gift sync re-checks this count afterwards.
      if (gift) items.push({ id: Number(gift.dataset.giftId), quantity: Number(selectedBundle().dataset.qty) });

      try {
        // Straight to checkout: start from an empty cart so the order is exactly the chosen bundle.
        if (directCheckout) await fetch(root.dataset.clearUrl, { method: 'POST', headers: JSON_HEADERS });
        const response = await fetch(root.dataset.addUrl, {
          method: 'POST',
          headers: JSON_HEADERS,
          body: JSON.stringify({ items }),
        });
        if (!response.ok) throw new Error(await response.text());

        // The theme editor can't load checkout inside its preview frame.
        if (directCheckout && window.Shopify?.designMode) {
          showMessage(root.dataset.editorNote);
          setBusy(false);
          return;
        }

        window.cirroRedirecting = true;
        window.location.assign(root.dataset.afterAddUrl);
        // Never leave the button stuck if the browser refuses the navigation.
        setTimeout(() => setBusy(false), 6000);
      } catch (error) {
        console.error('[cirro] add to cart failed', error);
        setBusy(false);
        showMessage(root.dataset.errorNote);
      }
    });

    // Re-enable the buttons if the shopper comes back with the browser's Back button.
    window.addEventListener('pageshow', () => setBusy(false));

    // Estimated arrival
    const eta = root.querySelector('[data-cirro-eta]');
    if (eta) {
      const format = (d) => d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
      const now = new Date();
      eta.textContent = `${format(addBusinessDays(now, 5))} – ${format(addBusinessDays(now, 8))}`;
    }

    // Sticky bar appears once the main button scrolls out of view.
    if (sticky && mainButton && 'IntersectionObserver' in window) {
      new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting && entry.boundingClientRect.top < 0);
      }).observe(mainButton);
    }
  };

  const initAll = (scope = document) => scope.querySelectorAll('[data-cirro-product]').forEach(init);
  initAll();
  document.addEventListener('DOMContentLoaded', () => initAll());
  // Theme editor re-renders sections without a page load.
  document.addEventListener('shopify:section:load', (event) => initAll(event.target));
})();
